import { useState } from 'react';

import useStateHandler from '@/hooks/useStateHandler'; // Import the state handler for temporal popups
import AuthService from '@/services/auth.service';
import UserService from '@/services/user.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import CustomSpinner from '@/shared-components/loaders/components/CustomSpinner';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import Text from '@/shared-components/text/components/Text';

import './styles/DeleteAccountDialog.css';

export default function DeleteAccountPopup({ open, setOpen }) {
  // Get the state handler to trigger temporal popups
  const { stateHandler } = useStateHandler();
  
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Handler for the Delete button
  const onDeleteClick = async () => {
    // Trigger temporal popup for deletion process
    stateHandler.set("temporalPopup", { 
      text: "Account deletion process started", 
    });
    
    setLoading(true);
    await UserService.deleteAccount();
    setTimeout(() => {
      setLoading(false);
      setShowConfirmation(true);
    }, 2000); // 2 seconds delay
  };

  // Handler for the Stay button
  const onStayClick = () => {
    // Trigger temporal popup for staying
    stateHandler.set("temporalPopup", { 
      text: "Thank you for your trust", 
    });
    // Close the popup
    setOpen(false);
  };

  // Handler for closing the popup (used by the X icon and the Close button in confirmation mode)
  const onCloseClick = () => {
    if (!loading) {
      if (!showConfirmation) {
        setOpen(false);
      } else {
        AuthService.logout();
      }
    }
  };

  return (
    <GenericPopup
      open={open}
      className="popup-confirm-delete-account_popup"
      opacity
    >
      <div className="popup-confirm-delete-account-container">
        <div className="popup-confirm-delete-account_header">
          <Text className="popup-confirm-delete-account_header-text">
            {!showConfirmation ? 'Delete Account' : 'Account Deleted'}
          </Text>
          <XIcon
            className="popup-confirm-delete-account_close"
            onClick={onCloseClick}
          />
        </div>

        <Text className="popup-confirm-delete-account_text">
          {!showConfirmation ? (
            <>
              Deleting your account results in permanent data loss. For temporary deactivation assistance, contact us at
              <a className="popup-confirm-delete-account_link" href="mailto:contact@suarte.art"> contact@suarte.art</a>
            </>
          ) : (
            <>
              <p>
                We’re deeply thankful for the time you’ve shared with us at Suarte. Every creation and contribution has enriched Suarte's community, and your presence has truly made a difference. We understand that this decision wasn’t made lightly, and we appreciate every moment you chose to invest in our journey together.
              </p>
              <p>
                It saddens us to see you go, and with a heavy heart, we confirm that all your progress will be permanently erased within 24 hours. While we respect your decision, please know that you will be dearly missed, and our door is always open if you ever wish to return.
              </p>
              <p>
                If you change your mind during this time, contact us at <a className="popup-confirm-delete-account_link" href="mailto:contact@suarte.art">contact@suarte.art</a>
              </p>
              <p>
                Thank you
              </p>
            </>
          )}
        </Text>

        <div className="popup-confirm-delete-account_buttons">
          {!showConfirmation ? (
            <>
              <ContinueButton
                onClick={onDeleteClick}
                medium
                disabled={loading}
                className="continue-button_two"  // Added class here
              >
        Delete
                {loading && (
                  <CustomSpinner
                    className="popup-confirm-delete-account_spinner"
                    thin
                  />
                )}
              </ContinueButton>
              <ContinueButton
                onClick={onStayClick}
                medium
                className="continue-button_two"  // And added here as well
              >
        Stay
              </ContinueButton>
            </>
          ) : (
            <ContinueButton onClick={onCloseClick} medium>
      Close
            </ContinueButton>
          )}
        </div>

      </div>
    </GenericPopup>
  );
}
